import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";

import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";
import ClientLayout from "@/components/ClientLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/public/Home";
import Services from "@/pages/public/Services";
import Portfolio, { PortfolioDetail } from "@/pages/public/Portfolio";
import Shop, { ProductDetail } from "@/pages/public/Shop";
import { About, Contact, LegalPage } from "@/pages/public/AboutContact";
import QuoteWizard from "@/pages/public/QuoteWizard";
import { Cart, Checkout } from "@/pages/public/Cart";

import { Login, Register, ForgotPassword, ResetPassword, AuthCallback } from "@/pages/auth/Auth";

import {
  ClientDashboard, ClientQuotes, ClientQuoteDetail, ClientProposals, ClientProposalDetail,
  ClientProjects, ClientOrders, ClientOrderDetail, ClientFiles,
} from "@/pages/client/Client";

import {
  AdminDashboard, AdminQuotes, AdminQuoteDetail, AdminProposals, AdminProjects,
  AdminOrders, AdminOrderDetail, AdminProducts, AdminProductForm,
  AdminPrinters, AdminMaterials, AdminProduction, AdminCustomers,
  AdminNotifications, AdminSettings, AdminShipments,
} from "@/pages/admin/Admin";
import {
  AdminSchedule, AdminTime, AdminQuality, AdminConsumption, AdminExports, AdminIntegrations,
} from "@/pages/admin/AdminExtras";
import Materials from "@/pages/public/Materials";
import STLAnalyzer from "@/pages/public/STLAnalyzer";

import "@/index.css";

function AppRouter() {
  const loc = useLocation();
  // Handle Emergent Google OAuth callback synchronously during render (fragment #session_id=…)
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/servicos" element={<PublicLayout><Services /></PublicLayout>} />
      <Route path="/portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
      <Route path="/portfolio/:slug" element={<PublicLayout><PortfolioDetail /></PublicLayout>} />
      <Route path="/loja" element={<PublicLayout><Shop /></PublicLayout>} />
      <Route path="/loja/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
      <Route path="/sobre" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/contactos" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/pedido-orcamento" element={<PublicLayout><QuoteWizard /></PublicLayout>} />
      <Route path="/materiais" element={<PublicLayout><Materials /></PublicLayout>} />
      <Route path="/ferramentas/analise-stl" element={<PublicLayout><STLAnalyzer /></PublicLayout>} />
      <Route path="/carrinho" element={<PublicLayout><Cart /></PublicLayout>} />
      <Route path="/checkout" element={<PublicLayout><Checkout /></PublicLayout>} />
      <Route path="/legal/termos" element={<PublicLayout><LegalPage slug="termos" /></PublicLayout>} />
      <Route path="/legal/privacidade" element={<PublicLayout><LegalPage slug="privacidade" /></PublicLayout>} />
      <Route path="/legal/ficheiros" element={<PublicLayout><LegalPage slug="ficheiros" /></PublicLayout>} />
      <Route path="/legal/envios" element={<PublicLayout><LegalPage slug="envios" /></PublicLayout>} />
      <Route path="/legal/devolucoes" element={<PublicLayout><LegalPage slug="devolucoes" /></PublicLayout>} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/registo" element={<Register />} />
      <Route path="/recuperar-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Client portal */}
      <Route path="/portal" element={<ProtectedRoute><ClientLayout><ClientDashboard /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/orcamentos" element={<ProtectedRoute><ClientLayout><ClientQuotes /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/orcamentos/:id" element={<ProtectedRoute><ClientLayout><ClientQuoteDetail /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/propostas" element={<ProtectedRoute><ClientLayout><ClientProposals /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/propostas/:id" element={<ProtectedRoute><ClientLayout><ClientProposalDetail /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/projetos" element={<ProtectedRoute><ClientLayout><ClientProjects /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/encomendas" element={<ProtectedRoute><ClientLayout><ClientOrders /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/encomendas/:id" element={<ProtectedRoute><ClientLayout><ClientOrderDetail /></ClientLayout></ProtectedRoute>} />
      <Route path="/portal/ficheiros" element={<ProtectedRoute><ClientLayout><ClientFiles /></ClientLayout></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute staffOnly><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/orcamentos" element={<ProtectedRoute staffOnly><AdminLayout><AdminQuotes /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/orcamentos/:id" element={<ProtectedRoute staffOnly><AdminLayout><AdminQuoteDetail /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/propostas" element={<ProtectedRoute staffOnly><AdminLayout><AdminProposals /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/projetos" element={<ProtectedRoute staffOnly><AdminLayout><AdminProjects /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/encomendas" element={<ProtectedRoute staffOnly><AdminLayout><AdminOrders /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/encomendas/:id" element={<ProtectedRoute staffOnly><AdminLayout><AdminOrderDetail /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/producao" element={<ProtectedRoute staffOnly><AdminLayout><AdminProduction /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/impressoras" element={<ProtectedRoute staffOnly><AdminLayout><AdminPrinters /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/materiais" element={<ProtectedRoute staffOnly><AdminLayout><AdminMaterials /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/produtos" element={<ProtectedRoute staffOnly><AdminLayout><AdminProducts /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/produtos/:id" element={<ProtectedRoute staffOnly><AdminLayout><AdminProductForm /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/expedicoes" element={<ProtectedRoute staffOnly><AdminLayout><AdminShipments /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/clientes" element={<ProtectedRoute staffOnly><AdminLayout><AdminCustomers /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/notificacoes" element={<ProtectedRoute staffOnly><AdminLayout><AdminNotifications /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/definicoes" element={<ProtectedRoute staffOnly><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/planeamento" element={<ProtectedRoute staffOnly><AdminLayout><AdminSchedule /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/registo-horas" element={<ProtectedRoute staffOnly><AdminLayout><AdminTime /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/qualidade" element={<ProtectedRoute staffOnly><AdminLayout><AdminQuality /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/consumos" element={<ProtectedRoute staffOnly><AdminLayout><AdminConsumption /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/exports" element={<ProtectedRoute staffOnly><AdminLayout><AdminExports /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/integracoes" element={<ProtectedRoute staffOnly><AdminLayout><AdminIntegrations /></AdminLayout></ProtectedRoute>} />

      <Route path="*" element={<PublicLayout><div className="container-x py-20"><p className="tech-label">404</p><h1 className="text-4xl font-bold">Página não encontrada</h1></div></PublicLayout>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
